import React, { useEffect, useRef, useState } from "react";
import {
  HeaderLinksBase,
  LinksContainer,
  DrawerTitleLink,
  HeaderLinkIcon,
  PageLink,
  DrawerLink,
  BackButton,
  HeaderLinkIconMobile,
} from "./header-links-legacy.styled";
import {
  HeaderLinksQuery,
  HeaderLinksNodeFragment,
  PrismicHeaderLinksDataBodyDrawerColumn,
  PrismicHeaderLinksDataBodyDrawerButtonPrimary,
} from "../../types/graphql";
import { PrismicRichText } from "@prismicio/react";
import { htmlSerializer } from "../../utils/htmlSerializer";
import { buildRelativeUrl } from "../../utils/environment-urls";
import * as FA from "../../styles/fontawesome";
import { useThemeContext } from "../../utils/contexts/theme-context";
import clsx from "clsx";

interface IPureHeaderLinksProps {
  headerLinks: HeaderLinksQuery;
  headerLinksUID: string;
  setHeaderLinksData: (data: IStructuredHeaderLinksData) => void;
  isHeaderLinksDataSet: boolean;
  isOpen: boolean;
  showAnnouncementBanner: boolean;
  isDarkMode?: boolean;
  setIsDesktopNavOpen: (isOpen: boolean) => void;
  // Accepted for prop-compatibility with the new nav so the wrapper can pass
  // one identical prop object to either variant. Unused by the legacy nav.
  registerEscapeHandler?: (handler: (() => boolean) | null) => void;
  onRequestCloseMobileNav?: () => void;
}

export interface IDrawerData {
  title: string | null | undefined;
  columns: PrismicHeaderLinksDataBodyDrawerColumn[];
}

export interface IStructuredHeaderLinksData {
  drawers: IDrawerData[];
  pageLinks?: Array<{
    link_text?: string | null;
    link?: { target?: string | null; url?: string | null } | null;
  }>;
}

// Function to extract array of drawer objects.
const getDrawerDataStructure = (
  links?: HeaderLinksNodeFragment
): IDrawerData[] => {
  let drawerData: IDrawerData[] = [];

  let i = 0;
  let slices = links?.data?.body;
  while (slices && i < slices.length && slices[i]) {
    if (slices[i]?.slice_type === "drawer_button") {
      // Push a drawer object onto result array with title and empty columns array
      drawerData.push({
        title: (
          slices[i]?.primary as PrismicHeaderLinksDataBodyDrawerButtonPrimary
        )?.drawer_button_text,
        columns: [],
      });
      let drawerColumns = [];
      i++;
      // Loop over all drawer column slices pushing column data into columns array
      while (i < slices.length && slices[i]?.slice_type !== "drawer_button") {
        if (slices[i]?.slice_type === "drawer_column")
          drawerColumns.push(
            slices[i] as PrismicHeaderLinksDataBodyDrawerColumn
          );
        i++;
      }
      drawerData[drawerData.length - 1].columns.push(...drawerColumns);
    }
  }

  return drawerData;
};

// This component contains all the markup and logic for the drawers
// and page links that appear on the header. Data is only passec
export const PureHeaderLinksLegacy = (props: IPureHeaderLinksProps) => {
  // Current open drawer
  const [openIndex, setOpenIndex] = useState(-1);
  const headerLinkRef: any = useRef(null);

  // Used to calcualte height of drawer for styling purposes
  const [drawerHeight, setDrawerHeight] = useState(0);
  const drawerRef: any = useRef(null);
  const drawerContentRef: any = useRef(null);

  const [openDrawerItems, setOpenDrawerItems] = useState(-1);

  const {
    headerLinks,
    headerLinksUID,
    setHeaderLinksData,
    isHeaderLinksDataSet,
    setIsDesktopNavOpen,
  } = props;

  const isDarkMode = props.isDarkMode || false;

  const headerEntries = headerLinks.allPrismicHeaderLinks.edges;

  const { currentTransitionPercent } = useThemeContext();

  const [headerHeight, setHeaderHeight] = useState(0);
  const [mainHeaderHeight, setMainHeaderHeight] = useState(0);

  useEffect(() => {
    const header = document.getElementById("site-header");
    const mainHeader = document.getElementById("site-main-header");

    // Handler to call on window resize
    const handleResize = () => {
      setTimeout(() => {
        if (header?.offsetHeight) {
          setHeaderHeight(header?.offsetHeight);
        }
        if (mainHeader?.offsetHeight) {
          setMainHeaderHeight(mainHeader?.offsetHeight);
        }
      }, 500);
    };

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // console.log('currentTransitionPercent', currentTransitionPercent);
  // Get the data for the corresponding header entry passed in from template. If not found,
  // it defaults to the default entry which is assumed to exist.
  const selectedEntry =
    headerEntries?.find((edge) => edge?.node?.uid === headerLinksUID) ||
    headerEntries?.find((edge) => edge?.node.uid === `default-links`);

  const drawers = getDrawerDataStructure(selectedEntry?.node);

  useEffect(() => {
    // Set the structured drawer data in state of <Header/> component
    !isHeaderLinksDataSet &&
      setHeaderLinksData &&
      setHeaderLinksData({
        drawers: drawers,
        pageLinks: selectedEntry?.node.data?.page_links,
      });

    document.addEventListener("mousedown", handleOutsideClick);

    if (openIndex != -1 && drawerContentRef.current) {
      // setDrawerHeight(drawerContentRef.current.clientHeight);
      setDrawerHeight(500);
    }

    return () => {
      window.removeEventListener("mousedown", handleOutsideClick);
    };
  });

  // Closes drawer if user clicked outside of the header
  const handleOutsideClick = (e: MouseEvent) => {
    if (
      !headerLinkRef?.current?.contains(e.target) &&
      !drawerRef?.current?.contains(e.target)
    ) {
      setOpenIndex(-1);
    }
  };

  const handleLinkInteraction = (index: number) => {
    openIndex != index ? setOpenIndex(index) : setOpenIndex(-1);
  };

  useEffect(() => {
    setIsDesktopNavOpen(openIndex !== -1);
  }, [openIndex]);

  return (
    <HeaderLinksBase
      isOpen={props.isOpen}
      showOverlay={openIndex !== -1}
      showAnnouncementBanner={props.showAnnouncementBanner}
      isDarkMode={isDarkMode}
      offsetTop={headerHeight}
    >
      {/* Flexbox for the Drawer Title Buttons and Page Links */}
      <LinksContainer
        ref={headerLinkRef}
        isOpen={openIndex !== -1}
        isOpenMobile={props.isOpen}
      >
        {/* Drawer Title Buttons */}
        {drawers.map((drawer, index) => {
          return (
            drawer &&
            drawer.title && (
              <DrawerTitleLink
                key={index}
                tabIndex={0}
                isOpen={openIndex === index}
                isDarkMode={isDarkMode}
                transitionPercent={currentTransitionPercent}
                offsetTop={headerHeight}
                offsetTopMainHeader={mainHeaderHeight}
              >
                <span
                  onClick={() => handleLinkInteraction(index)}
                  onKeyPress={() => handleLinkInteraction(index)}
                >
                  {drawer.title}

                  {openIndex == index ? (
                    <HeaderLinkIcon css={FA.AngleUpSolid}></HeaderLinkIcon>
                  ) : (
                    <HeaderLinkIcon css={FA.AngleDownSolid}></HeaderLinkIcon>
                  )}
                  <HeaderLinkIconMobile
                    css={FA.AngleRightSolid}
                  ></HeaderLinkIconMobile>
                </span>
                <div className={openIndex === index ? "drawer open" : "drawer"}>
                  <BackButton onClick={() => setOpenIndex(-1)}>
                    <i css={FA.AngleLeftRegular}></i>
                  </BackButton>
                  <ul
                    className={clsx([
                      props.headerLinksUID, // Add UID for styling purposes
                      `drawer-${index}`,
                    ])}
                  >
                    {drawers[index].columns.map(
                      (column, index) =>
                        column && (
                          <li key={index}>
                            <span
                              onClick={() =>
                                openDrawerItems === index
                                  ? setOpenDrawerItems(-1)
                                  : setOpenDrawerItems(index)
                              }
                            >
                              {column.primary?.column_link &&
                              column.primary?.column_link.url ? (
                                <a
                                  href={buildRelativeUrl(
                                    column.primary?.column_link.url
                                  )}
                                  target={
                                    column.primary?.column_link.target || ""
                                  }
                                  rel={
                                    column.primary?.column_link.target ===
                                    "_blank"
                                      ? "noopener noreferrer"
                                      : "noreferrer"
                                  }
                                >
                                  {column.primary?.column_title}
                                </a>
                              ) : (
                                column.primary?.column_title
                              )}
                            </span>
                            {column.items && (
                              <ul
                                className={
                                  index === openDrawerItems ? "open" : ""
                                }
                                style={{
                                  columns: column.primary?.column_count || 1,
                                }}
                              >
                                {column.items?.map((item, index) => {
                                  return (
                                    item?.link && (
                                      <li key={index}>
                                        <DrawerLink
                                          onClick={() => {
                                            setOpenIndex(-1);
                                            setOpenDrawerItems(-1);
                                          }}
                                        >
                                          <PrismicRichText
                                            field={item.link.richText}
                                            components={htmlSerializer}
                                          />
                                        </DrawerLink>
                                      </li>
                                    )
                                  );
                                })}
                              </ul>
                            )}
                          </li>
                        )
                    )}
                  </ul>
                </div>
              </DrawerTitleLink>
            )
          );
        })}

        {/* Page Links */}
        {selectedEntry?.node.data?.page_links?.map(
          (pageLink, index) =>
            pageLink?.link_text &&
            pageLink?.link?.url && (
              <li key={index}>
                <PageLink
                  href={buildRelativeUrl(pageLink.link.url)}
                  target={pageLink.link.target || ""}
                  isDarkMode={isDarkMode}
                  transitionPercent={currentTransitionPercent}
                  isOpen={openIndex !== -1}
                  offsetTop={headerHeight}
                  offsetTopMainHeader={mainHeaderHeight}
                >
                  {pageLink.link_text}
                </PageLink>
              </li>
            )
        )}
      </LinksContainer>
    </HeaderLinksBase>
  );
};
